import { useMemo, useState, type JSX } from 'react'
import { Column } from 'primereact/column'
import { confirmDialog } from 'primereact/confirmdialog'
import {
  Eye, Pencil, Pause, Play, Database, Plus, Search, RefreshCw,
  Building2, CheckCircle2, AlertCircle, Server, Trash2
} from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import DataTable from '../../components/ui/DataTable.js'
import { ProvisionWizardDrawer } from '../../components/platform/ProvisionWizardDrawer.js'
import { useTenants, type Tenant } from '../../hooks/useTenants.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatDate } from '../../utils/formatDate.js'
import { usePlanLookup } from '../../hooks/usePlans.js'

const STATUS_OPTIONS = [
  { label: 'All', value: null as string | null },
  { label: 'Active', value: 'active' },
  { label: 'Suspended', value: 'suspended' },
]

/* ─── Status Badge ─── */
function StatusBadge({ status }: { status: string }) {
  const isActive = status === 'active'
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
        isActive
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-amber-50 text-amber-700'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof Building2
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div
          className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: `${accent}10` }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: accent }}
      />
    </div>
  )
}

/* ─── Plan Name Helper ─── */
function getPlanName(planId: string | null, planMap: Map<string, string>): string {
  if (!planId) return '—'
  const name = planMap.get(planId)
  if (name) return name
  // If planId looks like a UUID, it's an unknown/missing plan
  if (planId.includes('-') && planId.length > 20) return 'Custom Plan'
  return planId
}

/* ─── Plan Badge ─── */
function PlanBadge({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-semibold bg-[#3454cf]/5 text-[#3454cf]">
      {name}
    </span>
  )
}

/* ─── Action Button ─── */
function ActionIcon({ icon: Icon, label, onClick, color }: {
  icon: typeof Eye
  label: string
  onClick: () => void
  color: string
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 transition-all duration-150 hover:border-transparent hover:text-white"
      style={{ ['--color' as any]: color }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = (e.currentTarget as HTMLElement).style.getPropertyValue('--color')
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'
        ;(e.currentTarget as HTMLElement).style.borderColor = '#f3f4f6'
      }}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

/* ─── Tenant Avatar ─── */
function TenantAvatar({ name }: { name: string }) {
  const initial = name?.charAt(0).toUpperCase() || 'T'
  const hues = [52, 220, 142, 280, 25, 190]
  const hue = hues[(name?.length || 0) % hues.length]
  return (
    <div
      className="h-10 w-10 rounded-sm flex items-center justify-center text-white text-sm font-bold shrink-0"
      style={{ background: `hsl(${hue}, 65%, 45%)` }}
    >
      {initial}
    </div>
  )
}

export default function TenantsPage(): JSX.Element {
  const toast = useToast()
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [wizardOpen, setWizardOpen] = useState(false)
  const [wizardMode, setWizardMode] = useState<'create' | 'view' | 'edit'>('create')
  const [selectedTenant, setSelectedTenant] = useState<Tenant | undefined>()

  const { items, loading, refetch, update, provision, deleteItem } = useTenants(
    statusFilter ? { status: statusFilter } : undefined,
  )
  const { items: plans } = usePlanLookup()

  const planMap = useMemo(() => {
    const map = new Map<string, string>()
    plans.forEach(p => map.set(p.id, p.name))
    return map
  }, [plans])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(t => t.slug.toLowerCase().includes(q) || t.name.toLowerCase().includes(q))
  }, [items, search])

  const activeCount = items.filter(t => t.status === 'active').length
  const suspendedCount = items.filter(t => t.status === 'suspended').length
  const provisionedCount = items.filter(t => t.schemaProvisioned).length

  function handleSuspend(t: Tenant) {
    confirmDialog({
      message: 'Suspending will block all tenant logins. This is reversible via Reactivate.',
      header: `Suspend ${t.name}`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-warning',
      accept: async () => {
        try {
          await update(t.id, { status: 'suspended' })
          toast.success(`${t.name} suspended`)
          await refetch()
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Failed to suspend tenant')
        }
      },
    })
  }

  async function handleReactivate(t: Tenant) {
    try {
      await update(t.id, { status: 'active' })
      toast.success(`${t.name} reactivated`)
      await refetch()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to reactivate tenant')
    }
  }

  async function handleProvision(t: Tenant) {
    try {
      await provision(t.id)
      toast.success(`${t.name} provisioned`)
      await refetch()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to provision tenant')
    }
  }

  function handleDelete(t: Tenant) {
    confirmDialog({
      message: `Deleting "${t.name}" will permanently remove the tenant and all its data. This action cannot be undone.`,
      header: `Delete ${t.name}`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await deleteItem(t.id)
          toast.success(`${t.name} deleted`)
          await refetch()
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Failed to delete tenant')
        }
      },
    })
  }

  function openWizard(mode: 'create' | 'view' | 'edit', tenant?: Tenant) {
    setWizardMode(mode)
    setSelectedTenant(tenant)
    setWizardOpen(true)
  }

  return (
    <RequirePermission permission="platform:tenants_manage">
      <div className="w-full space-y-6 px-4">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Tenants</h1>
            <p className="text-sm text-gray-400 mt-1">Manage and monitor all platform tenants</p>
          </div>
          <button
            onClick={() => openWizard('create')}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
          >
            <Plus className="h-4 w-4" />
            Add Tenant
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Tenants" value={items.length} icon={Building2} accent="#3454cf" />
          <StatCard label="Active" value={activeCount} icon={CheckCircle2} accent="#10b981" />
          <StatCard label="Suspended" value={suspendedCount} icon={AlertCircle} accent="#f6931c" />
          <StatCard label="Provisioned" value={provisionedCount} icon={Server} accent="#8b5cf6" />
        </div>

        {/* Toolbar - separate from table */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="relative w-80">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
              <input
                type="text"
                placeholder="Search by name or slug..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 focus:outline-none transition-all"
              />
            </div>
            <div className="flex items-center bg-white border border-gray-200 rounded-sm p-1">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-4 py-2 text-xs font-semibold rounded-sm transition-all duration-150 ${
                    statusFilter === opt.value
                      ? 'bg-[#3454cf] text-white shadow-sm'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400 font-medium">{visible.length} tenants</span>
            <div className="w-px h-4 bg-gray-200" />
            <button
              onClick={() => refetch()}
              className="h-9 w-9 flex items-center justify-center rounded-sm text-gray-400 hover:text-[#3454cf] hover:bg-[#3454cf]/5 transition-all border border-gray-200 hover:border-[#3454cf]/20"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Table */}
        <DataTable
          value={visible}
          loading={loading}
          paginator
          rows={10}
          className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
          emptyMessage={search ? 'No matching tenants found' : 'No tenants yet. Create your first one.'}
        >
            <Column
              field="name"
              header="Tenant"
              sortable
              body={(t: Tenant) => (
                <div className="flex items-center gap-3 cursor-pointer group" onClick={() => openWizard('view', t)}>
                  <TenantAvatar name={t.name} />
                  <div>
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-[#3454cf] transition-colors">
                      {t.name}
                    </p>
                    <p className="text-xs text-gray-300 mt-0.5">{t.slug}</p>
                  </div>
                </div>
              )}
              style={{ width: '26%' }}
            />
            <Column
              field="planId"
              header="Plan"
              sortable
              body={(t: Tenant) => (
                <PlanBadge name={getPlanName(t.planId, planMap)} />
              )}
              style={{ width: '13%' }}
            />
            <Column
              field="status"
              header="Status"
              sortable
              body={(t: Tenant) => <StatusBadge status={t.status} />}
              style={{ width: '12%' }}
            />
            <Column
              field="schemaProvisioned"
              header="Schema"
              sortable
              body={(t: Tenant) =>
                t.schemaProvisioned ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    Ready
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                    Pending
                  </span>
                )
              }
              style={{ width: '12%' }}
            />
            <Column
              header="Recording"
              body={(t: Tenant) =>
                t.recordingConfig?.recordingEnabled ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    Yes
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                    No
                  </span>
                )
              }
              style={{ width: '8%' }}
            />
            <Column
              header="Retention Days"
              body={(t: Tenant) => (
                <span className="text-xs text-gray-400">
                  {t.recordingConfig?.recordingEnabled && t.recordingConfig.retentionDays != null
                    ? `${t.recordingConfig.retentionDays} days`
                    : '—'}
                </span>
              )}
              style={{ width: '12%' }}
            />
            <Column
              field="createdAt"
              header="Created"
              sortable
              body={(t: Tenant) => (
                <span className="text-xs text-gray-400">{formatDate(t.createdAt)}</span>
              )}
              style={{ width: '15%' }}
            />
            <Column
              header="Actions"
              body={(t: Tenant) => (
                <div className="flex items-center gap-1">
                  <ActionIcon icon={Eye} label="View" onClick={() => openWizard('view', t)} color="#3454cf" />
                  <ActionIcon icon={Pencil} label="Edit" onClick={() => openWizard('edit', t)} color="#3454cf" />
                  {t.status === 'active' ? (
                    <ActionIcon icon={Pause} label="Suspend" onClick={() => handleSuspend(t)} color="#f6931c" />
                  ) : (
                    <ActionIcon icon={Play} label="Activate" onClick={() => void handleReactivate(t)} color="#10b981" />
                  )}
                  {!t.schemaProvisioned && (
                    <ActionIcon icon={Database} label="Provision" onClick={() => void handleProvision(t)} color="#8b5cf6" />
                  )}
                  <ActionIcon icon={Trash2} label="Delete" onClick={() => handleDelete(t)} color="#ef4444" />
                </div>
              )}
              style={{ width: '140px' }}
            />
          </DataTable>

        <ProvisionWizardDrawer
          open={wizardOpen}
          mode={wizardMode}
          tenant={selectedTenant}
          onClose={() => {
            setWizardOpen(false)
            setWizardMode('create')
            setSelectedTenant(undefined)
            void refetch()
          }}
        />
      </div>
    </RequirePermission>
  )
}
